/**
 * Dashboard View
 * Summary cards, charts, and quick actions
 */

export class Dashboard {
  constructor(container) {
    this.container = container;
    this.transactions = [];
    this.monthlyData = {};
    this.charts = {};
    this.updateHandler = null;
    this.chartJsLoaded = false;
  }

  async render() {
    // Load Chart.js if not already loaded
    if (!window.Chart) {
      await this.loadChartJS();
    }

    this.container.innerHTML = `
      <div class="page-header">
        <h1 class="page-title">Dashboard</h1>
        <p class="page-subtitle">Visão geral do fluxo de caixa</p>
      </div>

      <div class="grid grid-cols-4">
        <div class="summary-card summary-card-entrada" data-navigate="/transactions?filter=entrada">
          <h3>Entradas</h3>
          <p class="amount" id="total-entradas">R$ 0,00</p>
          <span class="trend" id="trend-entradas"></span>
        </div>

        <div class="summary-card summary-card-saida" data-navigate="/transactions?filter=saida">
          <h3>Saídas</h3>
          <p class="amount" id="total-saidas">R$ 0,00</p>
          <span class="trend" id="trend-saidas"></span>
        </div>

        <div class="summary-card summary-card-saldo">
          <h3>Saldo Atual</h3>
          <p class="amount" id="saldo-atual">R$ 0,00</p>
          <span class="trend" id="trend-saldo"></span>
        </div>

        <div class="summary-card">
          <h3>Total de Lançamentos</h3>
          <p class="amount" id="total-lancamentos">0</p>
          <span class="subtitle" id="period-info"></span>
        </div>
      </div>

      <!-- Charts Row -->
      <div class="grid grid-cols-2" style="margin-top: var(--spacing-6);">
        <div class="card">
          <div class="card-header">
            <h2 class="card-title">Receitas vs Despesas</h2>
            <span class="badge badge-info">Últimos 6 meses</span>
          </div>
          <div class="card-body">
            <canvas id="dashboard-chart-income-expense" height="250"></canvas>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h2 class="card-title">Evolução do Saldo</h2>
            <span class="badge badge-info">Últimos 6 meses</span>
          </div>
          <div class="card-body">
            <canvas id="dashboard-chart-trend" height="250"></canvas>
          </div>
        </div>
      </div>

      <!-- Distribution Chart -->
      <div class="card" style="margin-top: var(--spacing-6);">
        <div class="card-header">
          <h2 class="card-title">Distribuição de Despesas por Categoria</h2>
          <span class="badge badge-secondary" id="expense-total-badge">R$ 0,00</span>
        </div>
        <div class="card-body">
          <div class="grid grid-cols-2">
            <canvas id="dashboard-chart-distribution" height="300"></canvas>
            <div id="category-legend" style="display: flex; flex-direction: column; justify-content: center; gap: var(--spacing-3);"></div>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="card" style="margin-top: var(--spacing-6);">
        <div class="card-header">
          <h2 class="card-title">Ações Rápidas</h2>
        </div>
        <div class="card-body">
          <div class="grid grid-cols-4">
            <button class="btn btn-primary" id="quick-new-transaction">
              ➕ Novo Lançamento
            </button>
            <button class="btn btn-secondary" id="quick-view-transactions">
              📋 Ver Transações
            </button>
            <button class="btn btn-secondary" id="quick-view-reports">
              📊 Análise Detalhada
            </button>
            <button class="btn btn-secondary" id="quick-export">
              📤 Exportar Backup
            </button>
          </div>
        </div>
      </div>
    `;

    this.attachEventListeners();
    this.loadData();

    // Listen for transaction updates (apenas uma vez)
    if (!this.updateHandler) {
      this.updateHandler = () => {
        console.log('📊 Dashboard: Atualizando valores...');
        this.loadData();
      };
      window.addEventListener('transactionsUpdated', this.updateHandler);
    }
  }

  async loadChartJS() {
    return new Promise((resolve, reject) => {
      if (window.Chart) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js';
      script.onload = () => {
        this.chartJsLoaded = true;
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  attachEventListeners() {
    // Make cards clickable
    const cards = this.container.querySelectorAll('[data-navigate]');
    cards.forEach(card => {
      card.addEventListener('click', () => {
        const path = card.dataset.navigate;
        if (window.app && window.app.router) {
          window.app.router.navigate(path);
        }
      });
    });

    // Quick actions
    const newTransactionBtn = this.container.querySelector('#quick-new-transaction');
    if (newTransactionBtn) {
      newTransactionBtn.addEventListener('click', () => {
        if (window.app && window.app.openNewTransactionModal) {
          window.app.openNewTransactionModal();
        }
      });
    }

    const viewTransactionsBtn = this.container.querySelector('#quick-view-transactions');
    if (viewTransactionsBtn) {
      viewTransactionsBtn.addEventListener('click', () => {
        if (window.app && window.app.router) {
          window.app.router.navigate('/transactions');
        }
      });
    }

    const viewReportsBtn = this.container.querySelector('#quick-view-reports');
    if (viewReportsBtn) {
      viewReportsBtn.addEventListener('click', () => {
        if (window.app && window.app.router) {
          window.app.router.navigate('/reports');
        }
      });
    }

    const exportBtn = this.container.querySelector('#quick-export');
    if (exportBtn) {
      exportBtn.addEventListener('click', () => {
        this.exportBackup();
      });
    }
  }

  loadData() {
    // Get transactions from localStorage
    const stored = localStorage.getItem('clinicaDaher_cashflow');
    if (stored) {
      try {
        this.transactions = JSON.parse(stored);
      } catch (e) {
        console.error('Error loading transactions:', e);
        this.transactions = [];
      }
    }

    // Filter last 6 months for charts
    this.filterLast6Months();
    this.processMonthlyData();
    this.updateSummary();
    this.renderCharts();
  }

  filterLast6Months() {
    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);
    const startDateStr = sixMonthsAgo.toISOString().split('T')[0];

    this.recentTransactions = this.transactions.filter(t => t.date >= startDateStr);
  }

  processMonthlyData() {
    this.monthlyData = {};

    this.recentTransactions.forEach(t => {
      const [year, month] = t.date.split('-');
      const monthKey = `${year}-${month}`;

      if (!this.monthlyData[monthKey]) {
        this.monthlyData[monthKey] = {
          entradas: {},
          saidas: {},
          totalEntradas: 0,
          totalSaidas: 0
        };
      }

      const amount = typeof t.amount === 'number' ? t.amount : parseFloat(t.amount) || 0;
      const monthData = this.monthlyData[monthKey];

      if (t.type === 'entrada') {
        monthData.totalEntradas += amount;
        monthData.entradas[t.category] = (monthData.entradas[t.category] || 0) + amount;
      } else if (t.type === 'saida') {
        monthData.totalSaidas += amount;
        monthData.saidas[t.category] = (monthData.saidas[t.category] || 0) + amount;
      }
    });
  }

  updateSummary() {
    // Calculate totals (all time)
    const totalEntradas = this.transactions
      .filter(t => t.type === 'entrada')
      .reduce((sum, t) => {
        const amount = typeof t.amount === 'number' ? t.amount : parseFloat(t.amount) || 0;
        return sum + amount;
      }, 0);

    const totalSaidas = this.transactions
      .filter(t => t.type === 'saida')
      .reduce((sum, t) => {
        const amount = typeof t.amount === 'number' ? t.amount : parseFloat(t.amount) || 0;
        return sum + amount;
      }, 0);

    const saldo = totalEntradas - totalSaidas;

    // Calculate trends (last month vs previous month)
    const trends = this.calculateTrends();

    // Update UI
    const entradasEl = this.container.querySelector('#total-entradas');
    const saidasEl = this.container.querySelector('#total-saidas');
    const saldoEl = this.container.querySelector('#saldo-atual');
    const totalEl = this.container.querySelector('#total-lancamentos');

    if (entradasEl) entradasEl.textContent = this.formatCurrency(totalEntradas);
    if (saidasEl) saidasEl.textContent = this.formatCurrency(totalSaidas);
    if (saldoEl) saldoEl.textContent = this.formatCurrency(saldo);
    if (totalEl) totalEl.textContent = this.transactions.length;

    // Update saldo card color based on positive/negative
    if (saldoEl) {
      saldoEl.style.color = saldo >= 0 ? 'var(--color-success-dark)' : 'var(--color-danger-dark)';
    }

    // Update trends
    const trendEntradas = this.container.querySelector('#trend-entradas');
    const trendSaidas = this.container.querySelector('#trend-saidas');
    const trendSaldo = this.container.querySelector('#trend-saldo');
    const periodInfo = this.container.querySelector('#period-info');

    if (trendEntradas) trendEntradas.innerHTML = trends.entradas;
    if (trendSaidas) trendSaidas.innerHTML = trends.saidas;
    if (trendSaldo) trendSaldo.innerHTML = trends.saldo;
    if (periodInfo && this.transactions.length > 0) {
      periodInfo.textContent = this.getDateRange();
      periodInfo.style.fontSize = 'var(--font-size-xs)';
      periodInfo.style.color = 'var(--gray-500)';
    }
  }

  calculateTrends() {
    const months = Object.keys(this.monthlyData).sort();
    if (months.length < 2) {
      return { entradas: '', saidas: '', saldo: '' };
    }

    const lastMonth = this.monthlyData[months[months.length - 1]];
    const prevMonth = this.monthlyData[months[months.length - 2]];

    const entradasChange = prevMonth.totalEntradas > 0
      ? ((lastMonth.totalEntradas - prevMonth.totalEntradas) / prevMonth.totalEntradas) * 100
      : 0;

    const saidasChange = prevMonth.totalSaidas > 0
      ? ((lastMonth.totalSaidas - prevMonth.totalSaidas) / prevMonth.totalSaidas) * 100
      : 0;

    const lastSaldo = lastMonth.totalEntradas - lastMonth.totalSaidas;
    const prevSaldo = prevMonth.totalEntradas - prevMonth.totalSaidas;
    const saldoChange = prevSaldo !== 0
      ? ((lastSaldo - prevSaldo) / Math.abs(prevSaldo)) * 100
      : 0;

    return {
      entradas: this.formatTrend(entradasChange),
      saidas: this.formatTrend(saidasChange),
      saldo: this.formatTrend(saldoChange)
    };
  }

  formatTrend(change) {
    if (Math.abs(change) < 1) return '';

    const icon = change > 0 ? '↗' : '↘';
    const color = change > 0 ? 'var(--color-success)' : 'var(--color-danger)';
    const sign = change > 0 ? '+' : '';

    return `<span style="color: ${color}; font-size: var(--font-size-sm);">${icon} ${sign}${change.toFixed(0)}%</span>`;
  }

  renderCharts() {
    if (!window.Chart) {
      console.error('Chart.js not loaded');
      return;
    }

    this.renderIncomeExpenseChart();
    this.renderTrendChart();
    this.renderDistributionChart();
  }

  renderIncomeExpenseChart() {
    const canvas = this.container.querySelector('#dashboard-chart-income-expense');
    if (!canvas) return;

    if (this.charts.incomeExpense) {
      this.charts.incomeExpense.destroy();
    }

    const months = Object.keys(this.monthlyData).sort();
    const labels = months.map(m => this.getMonthName(m));
    const entradas = months.map(m => this.monthlyData[m].totalEntradas);
    const saidas = months.map(m => this.monthlyData[m].totalSaidas);

    this.charts.incomeExpense = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Entradas',
            data: entradas,
            backgroundColor: 'rgba(16, 185, 129, 0.8)',
            borderColor: '#10B981',
            borderWidth: 2,
            borderRadius: 6
          },
          {
            label: 'Saídas',
            data: saidas,
            backgroundColor: 'rgba(239, 68, 68, 0.8)',
            borderColor: '#EF4444',
            borderWidth: 2,
            borderRadius: 6
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              font: {
                size: 12,
                weight: '600'
              },
              padding: 15,
              usePointStyle: true
            }
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: {
              size: 13,
              weight: '600'
            },
            bodyFont: {
              size: 12
            },
            callbacks: {
              label: (context) => {
                return context.dataset.label + ': ' + this.formatCurrency(context.parsed.y);
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            },
            ticks: {
              callback: (value) => 'R$ ' + value.toLocaleString('pt-BR')
            }
          },
          x: {
            grid: {
              display: false
            }
          }
        }
      }
    });
  }

  renderTrendChart() {
    const canvas = this.container.querySelector('#dashboard-chart-trend');
    if (!canvas) return;

    if (this.charts.trend) {
      this.charts.trend.destroy();
    }

    const months = Object.keys(this.monthlyData).sort();
    const labels = months.map(m => this.getMonthName(m));
    const saldos = months.map(m => this.monthlyData[m].totalEntradas - this.monthlyData[m].totalSaidas);

    // Calculate cumulative balance
    let cumulative = 0;
    const cumulativeSaldos = saldos.map(s => {
      cumulative += s;
      return cumulative;
    });

    this.charts.trend = new Chart(canvas, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Saldo Mensal',
            data: saldos,
            borderColor: '#3B82F6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: '#3B82F6',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2
          },
          {
            label: 'Saldo Acumulado',
            data: cumulativeSaldos,
            borderColor: '#10B981',
            backgroundColor: 'rgba(16, 185, 129, 0.05)',
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: '#10B981',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            borderDash: [5, 5]
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              font: {
                size: 12,
                weight: '600'
              },
              padding: 15,
              usePointStyle: true
            }
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: {
              size: 13,
              weight: '600'
            },
            bodyFont: {
              size: 12
            },
            callbacks: {
              label: (context) => {
                return context.dataset.label + ': ' + this.formatCurrency(context.parsed.y);
              }
            }
          }
        },
        scales: {
          y: {
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            },
            ticks: {
              callback: (value) => 'R$ ' + value.toLocaleString('pt-BR')
            }
          },
          x: {
            grid: {
              display: false
            }
          }
        }
      }
    });
  }

  renderDistributionChart() {
    const canvas = this.container.querySelector('#dashboard-chart-distribution');
    if (!canvas) return;

    if (this.charts.distribution) {
      this.charts.distribution.destroy();
    }

    // Aggregate all expenses by category
    const categoryTotals = {};
    Object.values(this.monthlyData).forEach(monthData => {
      Object.entries(monthData.saidas).forEach(([category, amount]) => {
        categoryTotals[category] = (categoryTotals[category] || 0) + amount;
      });
    });

    const categories = Object.keys(categoryTotals);
    const values = Object.values(categoryTotals);
    const colors = this.generateColors(categories.length);

    const totalExpenses = values.reduce((a, b) => a + b, 0);

    // Update expense total badge
    const badge = this.container.querySelector('#expense-total-badge');
    if (badge) {
      badge.textContent = this.formatCurrency(totalExpenses);
    }

    this.charts.distribution = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: categories,
        datasets: [{
          data: values,
          backgroundColor: colors,
          borderWidth: 3,
          borderColor: '#ffffff',
          hoverOffset: 10
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: {
              size: 13,
              weight: '600'
            },
            bodyFont: {
              size: 12
            },
            callbacks: {
              label: (context) => {
                const total = values.reduce((a, b) => a + b, 0);
                const percentage = ((context.parsed / total) * 100).toFixed(1);
                return context.label + ': ' + this.formatCurrency(context.parsed) + ' (' + percentage + '%)';
              }
            }
          }
        }
      }
    });

    // Render custom legend
    this.renderCategoryLegend(categories, values, colors, totalExpenses);
  }

  renderCategoryLegend(categories, values, colors, total) {
    const legendContainer = this.container.querySelector('#category-legend');
    if (!legendContainer) return;

    const sortedData = categories.map((cat, i) => ({
      category: cat,
      value: values[i],
      color: colors[i],
      percentage: (values[i] / total) * 100
    })).sort((a, b) => b.value - a.value);

    legendContainer.innerHTML = sortedData.map(item => `
      <div style="display: flex; align-items: center; gap: var(--spacing-3);">
        <div style="width: 16px; height: 16px; border-radius: 4px; background-color: ${item.color}; flex-shrink: 0;"></div>
        <div style="flex: 1; min-width: 0;">
          <div style="display: flex; justify-content: space-between; align-items: baseline; gap: var(--spacing-2);">
            <span style="font-weight: 600; font-size: var(--font-size-sm); color: var(--gray-800); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
              ${item.category}
            </span>
            <span style="font-size: var(--font-size-xs); color: var(--gray-600); white-space: nowrap;">
              ${item.percentage.toFixed(1)}%
            </span>
          </div>
          <div style="font-size: var(--font-size-xs); color: var(--gray-600);">
            ${this.formatCurrency(item.value)}
          </div>
        </div>
      </div>
    `).join('');
  }

  generateColors(count) {
    const colors = [
      '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6',
      '#EC4899', '#14B8A6', '#F97316', '#06B6D4', '#84CC16'
    ];

    const result = [];
    for (let i = 0; i < count; i++) {
      result.push(colors[i % colors.length]);
    }
    return result;
  }

  getMonthName(monthKey) {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
  }

  formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  exportBackup() {
    const data = {
      version: '2.0.0',
      exported_at: new Date().toISOString(),
      device_id: localStorage.getItem('clinicaDaher_deviceId') || 'unknown',
      transactions: this.transactions,
      metadata: {
        total_transactions: this.transactions.length,
        date_range: this.getDateRange()
      }
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fluxode-caixa-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);

    alert('✅ Backup exportado com sucesso!');
  }

  getDateRange() {
    if (this.transactions.length === 0) {
      return 'Nenhuma transação';
    }

    const dates = this.transactions
      .map(t => t.date)
      .filter(d => d)
      .sort();

    if (dates.length === 0) {
      return 'Sem datas';
    }

    const firstDate = new Date(dates[0]);
    const lastDate = new Date(dates[dates.length - 1]);

    return `${firstDate.toLocaleDateString('pt-BR')} até ${lastDate.toLocaleDateString('pt-BR')}`;
  }

  destroy() {
    // Destroy all charts
    Object.values(this.charts).forEach(chart => {
      if (chart) chart.destroy();
    });
    this.charts = {};

    // Remove event listener para evitar memory leaks
    if (this.updateHandler) {
      window.removeEventListener('transactionsUpdated', this.updateHandler);
      this.updateHandler = null;
    }
  }
}
